import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { getAuthorizationHeader } from '../../auth/helpers';
import { API_URL } from '../../config/config';

export const useMemberImage = (userId: string) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    const {
        data: blob,
        isLoading,
        error,
    } = useQuery<Blob, Error>({
        queryKey: ['memberImage', userId],
        queryFn: async () => {
            const response = await fetch(
                `${API_URL}/api/admin/users/${userId}/image`,
                {
                    headers: {
                        ...getAuthorizationHeader(),
                    },
                },
            );

            if (!response.ok) {
                throw new Error('Failed to fetch image');
            }

            return await response.blob();
        },
        enabled: !!userId,
        retry: 0,
    });

    /**
     * Create and cleanup object URL from blob
     * (object URLs must be explicitly revoked)
     */
    useEffect(() => {
        /**
         * Server responds with null for non-existent images.
         * Fetch converts that to 0-size blob.
         */
        if (!blob || blob.size === 0) {
            setImageUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [blob]);

    return { imageUrl, isLoading, error: error ?? null };
};
